/**
 * Instalment-plan types.
 *
 * Deliberately NOT added to the neighbouring `mockData.ts`. That file is the
 * de-facto type module for the Payments tab despite its name, and the disabled
 * `Fees` tab is what happens when fake data and real contracts share a home —
 * 737 lines of `MOCK_FEE_DATA` that reads as authoritative. New money types get
 * a file that says what it is.
 */

export type InstallmentFrequency =
  | "WEEKLY"
  | "FORTNIGHTLY"
  | "MONTHLY"
  | "QUARTERLY"
  | "HALF_YEARLY"
  | "YEARLY"
  | "CUSTOM";

export type InstallmentRounding = "LAST" | "FIRST";

export type InstallmentPlanStatus = "ACTIVE" | "COMPLETED" | "CANCELLED";

/**
 * `WAIVED` means "stop chasing this row", NOT "the customer owes less" — it
 * leaves the deal's net value alone. Writing money off stays what it already
 * is: an edit to the deal's discount.
 */
export type InstallmentStatus = "PENDING" | "PARTIAL" | "PAID" | "WAIVED";

export interface Installment {
  id: number;
  planId: number;
  /** 1-based position: "Instalment 7 of 24". */
  seq: number;
  /** ISO. The IST calendar day it falls due — treat as a DAY, not an instant. */
  dueDate: string;
  amount: number;
  /** Σ of the payments pointing at this row. Server-computed, never derived here. */
  paidAmount: number;
  waivedAmount: number;
  status: InstallmentStatus;
  settledAt: string | null;
  note?: string | null;
}

export interface InstallmentPlan {
  id: number;
  childId: number;
  status: InstallmentPlanStatus;
  /** What the schedule was generated FROM. Σ instalment amounts === this. */
  principal: number;
  installmentCount: number;
  frequency: InstallmentFrequency;
  intervalDays: number | null;
  rounding: InstallmentRounding;
  /** ISO — instalment #1's due date. */
  startDate: string;
  anchorDay: number;
  reminderDaysBefore: number;
  reminderHourIst: number;
  version: number;
  /**
   * Set when the deal's money moved after the plan was generated (a discount
   * edit, a re-attached course). The plan is then STALE, not wrong — the UI
   * shows a banner and offers a rebuild rather than silently re-spreading
   * amounts the customer was already quoted.
   */
  outOfSyncAt: string | null;
  note: string | null;
  installments: Installment[];

  /** Server-computed rollups. The client renders these, never recomputes them. */
  totalAmount: number;
  totalPaid: number;
  paidCount: number;
  overdueCount: number;
  overdueAmount: number;
  nextDueDate: string | null;
  nextDueAmount: number | null;
}

/** What `POST /core/installment/preview` returns — a schedule, written nowhere. */
export interface SchedulePreviewResponse {
  principal: number;
  installmentCount: number;
  rows: Array<{ seq: number; amount: number; dueDate: string }>;
  total: number;
  warnings: string[];
}

/** The per-catalog-item default. Shape only — never an amount. */
export interface InstallmentTemplate {
  id: number;
  nicheModelName: string;
  nicheItemId: number;
  name: string;
  installmentCount: number;
  frequency: InstallmentFrequency;
  intervalDays: number | null;
  rounding: InstallmentRounding;
  firstDueOffsetDays: number;
  reminderDaysBefore: number;
  reminderHourIst: number;
  isActive: boolean;
}

/** One line of an explicit multi-instalment settlement. */
export interface SettlementAllocation {
  installmentId: number;
  amount: number;
}

/** Display helpers — used by the strip, the table and the popup alike. */

export const INSTALLMENT_STATUS_LABEL: Record<InstallmentStatus, string> = {
  PENDING: "Pending",
  PARTIAL: "Part paid",
  PAID: "Paid",
  WAIVED: "Waived",
};

/**
 * How a row should read once the calendar is taken into account. `PENDING`
 * alone cannot distinguish "due next March" from "three weeks late", and those
 * are the two the operator most needs to tell apart.
 */
export type InstallmentDisplayState =
  | "paid"
  | "waived"
  | "partial"
  | "overdue"
  | "dueToday"
  | "upcoming";

export function installmentDisplayState(
  inst: Pick<Installment, "status" | "dueDate">,
  todayKey: string,
): InstallmentDisplayState {
  if (inst.status === "PAID") return "paid";
  if (inst.status === "WAIVED") return "waived";
  const dueKey = inst.dueDate.slice(0, 10);
  if (dueKey < todayKey) return "overdue";
  if (inst.status === "PARTIAL") return "partial";
  if (dueKey === todayKey) return "dueToday";
  return "upcoming";
}

/**
 * Existing `Badge` variants only — CLAUDE.md rule 6. Every one of these already
 * carries the meaning we want, so the EMI table reads like the rest of the app
 * instead of introducing a seventh colour vocabulary.
 */
export const INSTALLMENT_BADGE_VARIANT: Record<InstallmentDisplayState, string> = {
  paid: "verified",
  waived: "closed",
  partial: "waiting",
  overdue: "rejected",
  dueToday: "followup",
  upcoming: "gray",
};
